import * as React from "react";
import { useRef } from "react";
import { FDocenteteInterface } from "../../../interfaces/FDocenteteInterface";
import { useTranslation } from "react-i18next";
import { DialogProps, TextField, useMediaQuery } from "@mui/material";
import { objectToQuery, requestApi } from "../../../helpers/RequestApi";
import { GET, PATCH } from "../../../utils/MethodUtils";
import { FARTICLE_URL, FDOCENTETES_URL } from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import getErrorApi from "../../../helpers/GetErrorApi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import { useTheme } from "@mui/material/styles";
import { STORAGE_INDEXABLE } from "../../../utils/StorageUtils";
import {
  ELASTIC_STOCK_AVAILABLE,
  ELASTIC_STOCK_AVAILABLE_NOW,
} from "../../../utils/FArticleUtils";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import {
  FArticleInterface,
  FArticleSmallInterface,
} from "../../../interfaces/FArticleInterface";
import { FArticleSmallComponentLine } from "../fArticle/FArticleSmallComponent";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FArticleQuantityComponent from "../fArticle/FArticleQuantityComponent";
import { LoadingButton } from "@mui/lab";
import { SAGE_DO_TYPE_VENTE_PL } from "../../../utils/DoceneteteUtils";
import { set } from "../../../app/globalSlice";
import { isUserBonDeCommande } from "../../../helpers/UserHelper";

interface State {
  fDocentete: FDocenteteInterface;
}

interface State2 {
  fDocentete: FDocenteteInterface;
  fArticle: FArticleSmallInterface;
  handleClose: Function;
}

const AddFArticleComponent: React.FC<State2> = React.memo(
  ({ fDocentete, fArticle, handleClose }) => {
    const { t } = useTranslation();
    const [quantity, setQuantity] = React.useState(1);
    const [loading, setLoading] = React.useState(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const dispatch = useAppDispatch();
    const addToFDocentete = React.useCallback(async () => {
      setLoading(true);
      const response = await requestApi({
        method: PATCH,
        path: FDOCENTETES_URL + "/" + fDocentete.doPiece + "/add-farticle",
        allowError: false,
        token: token,
        body: {
          arRef: fArticle.arRef,
          quantity: quantity,
        },
      });
      if (response.statusCode === 200) {
        toastr.success(t("word.success"), t("word.success"));
        handleClose();
        dispatch(set({ refreshPage: true }));
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [
      dispatch,
      fArticle.arRef,
      fDocentete.doPiece,
      handleClose,
      quantity,
      t,
      token,
    ]);

    return (
      <Box>
        <FArticleSmallComponentLine
          nbColumn={1}
          fArticles={[fArticle]}
          showAddCart={false}
          defaultStyleInline={undefined}
          openNewTab={undefined}
          beforeNavigate={undefined}
        />
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 1 }}>
          <FArticleQuantityComponent
            fArticle={fArticle}
            setQuantityParent={setQuantity}
          />
          <LoadingButton
            variant="contained"
            onClick={addToFDocentete}
            loading={loading}
          >
            {t("word.addToOrder")}
          </LoadingButton>
        </Box>
        <Divider sx={{ marginY: 2 }} />
      </Box>
    );
  }
);

const AddFArticleFDocenteteComponent: React.FC<State> = React.memo(
  ({ fDocentete }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const [maxWidth] = React.useState<DialogProps["maxWidth"]>("md");
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const inputRef: any = useRef();
    const [searchFArticle, setSearchFArticle] = React.useState<string>("");
    const [loading, setLoading] = React.useState<boolean>(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const [fArticles, setFArticles] = React.useState<FArticleInterface[]>([]);
    const user = useAppSelector((state: RootState) => state.globalState.user);

    const handleClose = React.useCallback(() => {
      setOpen(false);
    }, []);
    const handleOpen = React.useCallback(() => {
      setOpen(true);
    }, []);

    const searchFArticles = React.useCallback(async () => {
      const keywords = searchFArticle.trim();
      if (keywords === "") {
        setLoading(false);
        return;
      }
      setLoading(true);
      let indexable = localStorage.getItem(STORAGE_INDEXABLE);
      if (indexable === null) {
        indexable = "1";
      }
      const param: any = {
        keywords: keywords,
        stock:
          fDocentete.doType === SAGE_DO_TYPE_VENTE_PL
            ? ELASTIC_STOCK_AVAILABLE_NOW
            : ELASTIC_STOCK_AVAILABLE,
        ...(indexable !== "" && { indexable: indexable }),
      };
      const response = await requestApi({
        method: GET,
        path: FARTICLE_URL + objectToQuery(param),
        allowError: false,
        paginate: false,
        token: token,
        timeout: 30_000,
      });
      if (searchFArticle !== inputRef?.current?.value) {
        // search has changed don't use this result
        return;
      }
      if (response.statusCode === 200) {
        setFArticles(response.content.fArticles);
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [fDocentete.doType, searchFArticle, t, token]);

    React.useEffect(() => {
      setFArticles([]);
      const timeoutTyping = setTimeout(() => {
        searchFArticles();
      }, 500);
      return () => clearTimeout(timeoutTyping);
    }, [searchFArticle]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        <Dialog
          maxWidth={maxWidth}
          fullWidth={true}
          fullScreen={fullScreen}
          onClose={handleClose}
          open={open}
        >
          <DialogContent>
            <TextField
              value={searchFArticle}
              onChange={(e) => setSearchFArticle(e.target.value)}
              placeholder="Rechercher..."
              fullWidth
              sx={{ marginBottom: 2 }}
              inputRef={inputRef}
              autoComplete="off"
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {loading && <CircularProgress size={20} />}
                  </InputAdornment>
                ),
              }}
            />
            {fArticles && (
              <>
                {fArticles.map((fArticle, i) => (
                  <AddFArticleComponent
                    key={fArticle.arRef}
                    fDocentete={fDocentete}
                    fArticle={fArticle}
                    handleClose={handleClose}
                  />
                ))}
              </>
            )}
          </DialogContent>
        </Dialog>
        {!isUserBonDeCommande(user) && (
          <Button
            variant="contained"
            onClick={handleOpen}
            sx={{ marginLeft: 2 }}
          >
            {t("word.addFArticle")}
          </Button>
        )}
      </>
    );
  }
);

export default AddFArticleFDocenteteComponent;
